import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH18_QUESTIONS } from "../data/neetPhysicsCh18Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 18: Current Electricity (NCERT Class 12 Physics).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Current & Drift Velocity", "Resistance & Ohm's Law", "Resistor & Cell Combinations", "Kirchhoff's Laws", "Wheatstone Bridge & Potentiometer"];

const SECTION_COLORS = {
  "Current & Drift Velocity":            "#2563eb",
  "Resistance & Ohm's Law":              "#7c3aed",
  "Resistor & Cell Combinations":        "#16a34a",
  "Kirchhoff's Laws":                    "#dc2626",
  "Wheatstone Bridge & Potentiometer":   "#d97706",
};

const CONCEPTS = [
  // ── Current & Drift Velocity ────────────────────────────────────────────────
  { section: "Current & Drift Velocity", title: "Electric current — definition, direction and SI unit",
    explanation: "Electric current is the rate of flow of electric charge through a cross-section of a conductor: I = Q/t (or i = dQ/dt for time-varying currents). It is a scalar quantity, though it is assigned a conventional direction -- the direction in which positive charge appears to flow. In a metal, current is actually carried by electrons moving opposite to this conventional direction. The SI unit of current is the ampere (A), where 1 A = 1 coulomb of charge flowing per second.",
    example: `I = Q / t          (charge per unit time)

If Q = 6 C flows past a cross-section in t = 3 s:
I = 6 / 3 = 2 A

Conventional current direction = direction of positive
charge flow = OPPOSITE to the actual drift of electrons in a metal` },
  { section: "Current & Drift Velocity", title: "Drift velocity of free electrons in a conductor",
    explanation: "In a conductor, free electrons move randomly at high thermal speeds (~10^5 m/s) but their net displacement is zero because collisions with the lattice randomize direction. When an electric field E is applied, each electron gains a small extra velocity between collisions; the average of this extra velocity over all free electrons is the drift velocity vd. Between successive collisions an electron accelerates for an average time called the relaxation time (tau), so vd = eE.tau/m (e = electron charge, m = electron mass).",
    example: `vd = eE.tau / m     (drift velocity, from a = eE/m acting for time tau)

Typical order of magnitude:
Thermal speed of electrons  ~ 10^5 m/s   (random, no net current)
Drift velocity vd           ~ 10^-4 m/s  (small net drift along -E)

Current flows almost instantly because the ELECTRIC FIELD
itself is set up at nearly the speed of light -- not because
individual electrons travel fast.` },
  { section: "Current & Drift Velocity", title: "Relation between current and drift velocity (I = nAvq)",
    explanation: "Consider a conductor of cross-sectional area A with n free charge carriers per unit volume, each of charge q, drifting with velocity vd. In a small time t, each carrier moves a distance vd.t, so all carriers within a cylinder of length vd.t and cross-section A cross a given plane. The number of carriers in this volume is n.A.vd.t, so the charge crossing per unit time gives current I = nAvd.q.",
    example: `Volume swept in time t = A x (vd . t)
Number of carriers in this volume = n . A . vd . t
Charge crossing in time t = (n A vd t) . q
Current I = charge / time = n A vd q

Example -- copper wire, A = 1 mm^2 = 1x10^-6 m^2, I = 1.5 A
n = 8.5x10^28 m^-3, q = 1.6x10^-19 C

vd = I / (n A q)
   = 1.5 / (8.5x10^28 x 1x10^-6 x 1.6x10^-19)
   = 1.5 / (1.36x10^4)
   ~ 1.1x10^-4 m/s   (extremely small drift speed)` },
  { section: "Current & Drift Velocity", title: "Current density",
    explanation: "Current density J at a point is the current per unit cross-sectional area, treated as a vector pointing along the direction of conventional current flow: J = I/A. In terms of microscopic quantities, J = nqvd. Current density relates to the electric field through the material's conductivity sigma via the microscopic form of Ohm's law, J = sigma.E (with resistivity rho = 1/sigma, so J = E/rho).",
    example: `J = I / A = n q vd         (SI unit: A/m^2)

Using the copper wire above (I = 1.5 A, A = 1x10^-6 m^2):
J = 1.5 / (1x10^-6) = 1.5x10^6 A/m^2

Check using J = n q vd:
J = (8.5x10^28)(1.6x10^-19)(1.1x10^-4) ~ 1.5x10^6 A/m^2   <- matches` },
  { section: "Current & Drift Velocity", title: "Mobility of charge carriers",
    explanation: "Mobility (mu) measures how readily a charge carrier drifts under an applied field: mu = vd/E, with SI unit m^2.V^-1.s^-1. Since vd = eE.tau/m, mobility can also be written mu = e.tau/m, and conductivity relates to mobility as sigma = n.q.mu.",
    example: `mu = vd / E = e.tau / m           (mobility)
sigma = n q mu                    (conductivity from mobility)

For copper: rho = 1.7x10^-8 Ohm.m -> sigma = 1/rho = 5.9x10^7 S/m
n = 8.5x10^28 m^-3, q = 1.6x10^-19 C

mu = sigma / (n q) = 5.9x10^7 / (8.5x10^28 x 1.6x10^-19)
   = 5.9x10^7 / 1.36x10^10 ~ 4.3x10^-3 m^2.V^-1.s^-1` },

  // ── Resistance & Ohm's Law ───────────────────────────────────────────────────
  { section: "Resistance & Ohm's Law", title: "Ohm's Law and ohmic/non-ohmic conductors",
    explanation: "Ohm's law states that, at constant temperature, the potential difference V across a conductor is directly proportional to the current I flowing through it: V = IR, where R (resistance) is constant for that conductor at that temperature. Conductors that obey this linear V-I relationship (metals, at constant T) are called OHMIC; devices whose V-I graph is non-linear or direction-dependent (junction diodes, thermistors, electrolytes with polarization) are called NON-OHMIC.",
    example: `V = I R        (Ohm's law; R constant at fixed temperature)

Ohmic:      V-I graph is a straight line through the origin;
            slope = R (constant)
Non-ohmic:  V-I graph is curved, or current does not reverse
            symmetrically with reversed voltage (e.g. p-n junction diode)

If R = 5 Ohm and V = 10 V:  I = V/R = 10/5 = 2 A` },
  { section: "Resistance & Ohm's Law", title: "Resistance, resistivity and conductivity",
    explanation: "The resistance of a uniform conductor of length L and cross-sectional area A is R = rho.L/A, where rho (resistivity) is an intrinsic property of the material, independent of its dimensions. Conductivity sigma = 1/rho measures how easily a material conducts. SI units: R in ohm (Ohm), rho in Ohm.m, sigma in siemens per metre (S/m).",
    example: `R = rho . L / A         (rho = resistivity, an intrinsic property)
sigma = 1 / rho          (conductivity)

Nichrome wire: L = 1 m, A = 0.5 mm^2 = 0.5x10^-6 m^2
rho(nichrome) = 1.1x10^-6 Ohm.m

R = (1.1x10^-6 x 1) / (0.5x10^-6) = 2.2 Ohm` },
  { section: "Resistance & Ohm's Law", title: "Temperature dependence of resistance",
    explanation: "For most metals, resistance/resistivity INCREASES with temperature: increased thermal vibration of the lattice ions reduces the relaxation time tau between electron collisions, reducing conductivity. This is captured by R_T = R_0[1 + alpha(T - T_0)], where alpha is the temperature coefficient of resistance (positive for metals, ~0.004 per degreeC for copper). For semiconductors, resistivity DECREASES with rising temperature (alpha is negative) because the number density of free charge carriers n increases sharply with temperature, an effect that outweighs the reduction in tau.",
    example: `R_T = R_0 [1 + alpha (T - T_0)]

Copper wire: R_0 = 10 Ohm at 20 C, alpha = 0.004 per C
Find R at 100 C:  deltaT = 80 C

R = 10 [1 + 0.004 x 80] = 10 [1 + 0.32] = 10 x 1.32 = 13.2 Ohm

(Semiconductors: alpha is NEGATIVE -- resistance FALLS as T rises)` },
  { section: "Resistance & Ohm's Law", title: "Colour code for resistors",
    explanation: "Fixed resistors are commonly marked with coloured bands instead of printed numbers. Each colour represents a digit: Black=0, Brown=1, Red=2, Orange=3, Yellow=4, Green=5, Blue=6, Violet=7, Grey=8, White=9. On a standard 4-band resistor, the first two bands give the significant digits, the third band is the power-of-ten multiplier (same colour code), and the fourth band gives the tolerance (Gold = +-5%, Silver = +-10%, no band = +-20%).",
    example: `Band colours (digit):  Black0 Brown1 Red2 Orange3 Yellow4
                        Green5 Blue6 Violet7 Grey8 White9

Resistor: Yellow - Violet - Orange - Gold
  Yellow = 4, Violet = 7, Orange = x10^3, Gold = +-5%

Value = 47 x 10^3 Ohm = 47 kOhm, tolerance +-5%` },
  { section: "Resistance & Ohm's Law", title: "Electrical energy, power and the heating effect of current (Joule's law)",
    explanation: "The electrical power delivered to (or dissipated in) a circuit element is P = VI, which for a resistor becomes P = I^2R = V^2/R using Ohm's law. The electrical energy consumed in time t is W = Pt. When current flows through a pure resistor, all the electrical energy delivered to it is converted into heat -- this is the heating effect of current, quantified by Joule's law: H = I^2Rt.",
    example: `P = V I = I^2 R = V^2 / R          (electrical power)
H = I^2 R t                        (Joule heating)

Bulb rated 100 W, 220 V (operating at rated voltage):
R = V^2/P = 220^2/100 = 484 Ohm
I = P/V = 100/220 ~ 0.455 A

Heat produced by a 2 A current through a 5 Ohm resistor in 10 min:
H = I^2 R t = (2)^2 x 5 x (10x60) = 4 x 5 x 600 = 12000 J` },

  // ── Resistor & Cell Combinations ────────────────────────────────────────────
  { section: "Resistor & Cell Combinations", title: "Resistors in series",
    explanation: "When resistors are connected end-to-end so that the SAME current flows through each of them, they are in series. The equivalent resistance is the simple sum: Rs = R1 + R2 + R3 + ..., and the total voltage across the combination divides among the individual resistors in proportion to their resistance (V = IR for each).",
    example: `Series:  Rs = R1 + R2 + R3 + ...     (same current I in each)

R1=2, R2=3, R3=5 Ohm in series, connected to a 10 V battery
Rs = 2+3+5 = 10 Ohm
I  = V/Rs = 10/10 = 1 A                (same through every resistor)
V1 = IR1 = 2 V,  V2 = IR2 = 3 V,  V3 = IR3 = 5 V
Check: V1+V2+V3 = 2+3+5 = 10 V = supply voltage` },
  { section: "Resistor & Cell Combinations", title: "Resistors in parallel",
    explanation: "When resistors are connected between the same pair of nodes so that the SAME potential difference appears across each, they are in parallel. The reciprocal of the equivalent resistance is the sum of reciprocals: 1/Rp = 1/R1 + 1/R2 + 1/R3 + ..., and the total current entering the combination divides among the branches inversely proportional to their resistance.",
    example: `Parallel:  1/Rp = 1/R1 + 1/R2 + 1/R3 + ...   (same voltage V across each)

R1=2, R2=3, R3=6 Ohm in parallel
1/Rp = 1/2 + 1/3 + 1/6 = 3/6 + 2/6 + 1/6 = 6/6 = 1  -> Rp = 1 Ohm

With a 6 V supply across the combination:
I1 = V/R1 = 6/2 = 3 A,  I2 = 6/3 = 2 A,  I3 = 6/6 = 1 A
Check: I1+I2+I3 = 3+2+1 = 6 A = total current from the source` },
  { section: "Resistor & Cell Combinations", title: "EMF, internal resistance and terminal voltage of a cell",
    explanation: "A cell's EMF (epsilon) is the work done per unit charge by the cell when no current flows (the open-circuit voltage). Every real cell has some internal resistance r, so when it drives a current I through an external resistance R, the terminal voltage (the voltage actually available across its terminals) is less than the EMF: V = epsilon - Ir. The current delivered is I = epsilon/(R + r).",
    example: `I = eps / (R + r)          V = eps - I r   (terminal voltage)

Cell: eps = 2 V, r = 0.5 Ohm, external R = 3.5 Ohm
I = 2 / (3.5 + 0.5) = 2/4 = 0.5 A
V = eps - I r = 2 - (0.5)(0.5) = 2 - 0.25 = 1.75 V

Check: V = I R = 0.5 x 3.5 = 1.75 V   <- matches` },
  { section: "Resistor & Cell Combinations", title: "Cells connected in series",
    explanation: "When n identical cells (each of EMF epsilon and internal resistance r) are connected in series (positive terminal of one to negative of the next), the same current flows through all of them. The effective EMF of the combination is n.epsilon, and the effective internal resistance is n.r.",
    example: `Series cells:  eps_eff = n.eps        r_eff = n.r
Current:  I = n.eps / (R + n.r)

3 identical cells, eps=1.5 V, r=0.2 Ohm each, external R=2.4 Ohm
eps_eff = 3 x 1.5 = 4.5 V
r_eff   = 3 x 0.2 = 0.6 Ohm
I = 4.5 / (2.4 + 0.6) = 4.5/3 = 1.5 A` },
  { section: "Resistor & Cell Combinations", title: "Cells connected in parallel",
    explanation: "When m identical cells (each of EMF epsilon and internal resistance r) are connected in parallel (all positive terminals joined, all negative terminals joined), the effective EMF of the combination remains epsilon (unchanged), but the effective internal resistance drops to r/m, since the m internal resistances are themselves in parallel. This arrangement is useful for delivering larger currents without raising the effective EMF.",
    example: `Parallel cells:  eps_eff = eps         r_eff = r/m
Current:  I = eps / (R + r/m)

2 identical cells, eps=1.5 V, r=0.5 Ohm each, external R=2 Ohm
r_eff = 0.5/2 = 0.25 Ohm
I = 1.5 / (2 + 0.25) = 1.5/2.25 ~ 0.67 A` },

  // ── Kirchhoff's Laws ─────────────────────────────────────────────────────────
  { section: "Kirchhoff's Laws", title: "Kirchhoff's Current Law (junction rule)",
    explanation: "Kirchhoff's Current Law (KCL) states that at any junction (node) in an electrical circuit, the algebraic sum of all currents meeting at that junction is zero -- equivalently, the total current flowing IN equals the total current flowing OUT. This follows directly from conservation of charge: in steady state, charge cannot accumulate at a junction.",
    example: `Sum of currents INTO a junction = Sum of currents OUT of it

At a junction: 3 A and 2 A flow IN; 4 A and an unknown I flow OUT
3 + 2 = 4 + I
I = 5 - 4 = 1 A` },
  { section: "Kirchhoff's Laws", title: "Kirchhoff's Voltage Law (loop rule)",
    explanation: "Kirchhoff's Voltage Law (KVL) states that the algebraic sum of all potential differences (including EMFs and IR drops) around any closed loop in a circuit is zero. This is a consequence of energy conservation -- since electric potential is a single-valued function of position, the net change in potential after returning to the starting point of a closed loop must be zero.",
    example: `Sum of potential changes around any closed loop = 0

Sign convention while traversing a loop in a chosen direction:
- Through a resistor ALONG assumed current: potential DROP (-IR)
- Through a resistor AGAINST assumed current: potential RISE (+IR)
- Through a cell from - to + terminal: potential RISE (+eps)
- Through a cell from + to - terminal: potential DROP (-eps)` },
  { section: "Kirchhoff's Laws", title: "Applying Kirchhoff's laws to a single loop",
    explanation: "For a simple single-loop circuit with one EMF source, internal resistance, and one or more external resistors, Kirchhoff's voltage law reduces to the familiar formula I = epsilon/(R_total). Writing out the loop equation explicitly is useful practice before tackling circuits with multiple loops and multiple EMF sources.",
    example: `Single loop: EMF eps=12 V, internal resistance r=1 Ohm,
external resistor R=5 Ohm, in series

KVL (going around the loop): eps - I.r - I.R = 0
12 - I(1) - I(5) = 0
12 = 6 I
I = 2 A` },
  { section: "Kirchhoff's Laws", title: "Solving a multi-loop, multi-EMF circuit using Kirchhoff's laws",
    explanation: "Circuits with more than one EMF source and more than one loop cannot generally be reduced by simple series-parallel rules -- they require Kirchhoff's laws. The method: assign an unknown current (with an assumed direction) to each branch, apply KCL at the junctions to relate the branch currents, then apply KVL to each independent loop to get enough equations to solve for every unknown current. A negative answer simply means the true current flows opposite to the assumed direction.",
    example: `Two cells in parallel, driving current the same way through
an external resistor R = 10 Ohm:
Cell 1: eps1 = 2 V, r1 = 1 Ohm       Cell 2: eps2 = 4 V, r2 = 2 Ohm
Let I1, I2 = currents from each cell; I = I1 + I2 through R (KCL)

Loop 1: eps1 - I1.r1 - I.R = 0  ->  2 - I1 - 10(I1+I2) = 0
Loop 2: eps2 - I2.r2 - I.R = 0  ->  4 - 2I2 - 10(I1+I2) = 0

Solving simultaneously:
I1 = -0.5 A   (actual direction opposite to assumed -- cell 1 is
               being charged rather than discharging)
I2 = 0.75 A
I  = I1 + I2 = 0.25 A   (current through the external resistor)` },

  // ── Wheatstone Bridge & Potentiometer ───────────────────────────────────────
  { section: "Wheatstone Bridge & Potentiometer", title: "Wheatstone bridge — construction and balance condition",
    explanation: "A Wheatstone bridge has four resistances P, Q, R, S arranged in a diamond/rhombus: P and Q form one pair of adjacent arms, R and S the other; a galvanometer connects the midpoint of the P-Q arm to the midpoint of the R-S arm, and a battery is connected across the opposite pair of corners. The bridge is BALANCED when no current flows through the galvanometer -- this happens when P/Q = R/S. At balance, the same current flows through P and Q (call it I1) and the same current flows through R and S (call it I2), and the potentials at the two galvanometer junctions are equal.",
    example: `Balance condition:  P/Q = R/S      (no current through galvanometer)

At balance:  I1.P = I2.R   and   I1.Q = I2.S
Dividing:    P/Q = R/S

Given P=4 Ohm, Q=6 Ohm, R=10 Ohm, find unknown S:
S = R.Q/P = (10 x 6)/4 = 15 Ohm` },
  { section: "Wheatstone Bridge & Potentiometer", title: "Meter bridge — a practical Wheatstone bridge",
    explanation: "A meter bridge (slide-wire bridge) is a practical realization of the Wheatstone bridge using a 1-metre long uniform resistance wire stretched over a scale, with a resistance box (R) in one gap and the unknown resistance (X) in the other. A jockey is slid along the wire to find the null point (balance point) where the galvanometer shows zero deflection, at a distance l cm from the end nearer X. The two wire segments (l and 100-l) act as the other two arms of the bridge.",
    example: `X / R = l / (100 - l)         X = R . l / (100 - l)

Known R = 10 Ohm, null point at l = 40 cm (from the X end)
X = 10 x 40 / (100 - 40) = 400 / 60 ~ 6.67 Ohm` },
  { section: "Wheatstone Bridge & Potentiometer", title: "Potentiometer — principle and potential gradient",
    explanation: "A potentiometer is a long uniform resistance wire connected to a steady 'driver' current from an auxiliary battery. Because the current is constant and the wire is uniform, the potential drop across any length of the wire is directly proportional to that length: V = k.l, where k = V/L is the potential gradient (volts per unit length). This lets a potentiometer measure an unknown EMF/potential difference by finding a length that balances it, drawing NO current from the source being measured at balance -- making it more accurate than a voltmeter for measuring EMF.",
    example: `k = V / L                (potential gradient, volts per metre)

Potentiometer wire, L = 400 cm, potential drop across full
length (from driver battery) = 8 V

k = 8 V / 400 cm = 0.02 V/cm  (= 2 V/m)` },
  { section: "Wheatstone Bridge & Potentiometer", title: "Potentiometer — comparing EMFs of two cells",
    explanation: "To compare the EMFs of two cells using a potentiometer (same driver circuit, so same potential gradient k for both measurements), each cell is connected (via a galvanometer and a two-way key) to the potentiometer wire and its balancing length is found: epsilon1 = k.l1 and epsilon2 = k.l2. Since k is common to both, the ratio of EMFs equals the ratio of their balancing lengths: epsilon1/epsilon2 = l1/l2.",
    example: `eps1 / eps2 = l1 / l2       (same potential gradient k for both)

Balance length for cell 1: l1 = 350 cm
Balance length for cell 2: l2 = 300 cm,  eps2 = 1.5 V (known)

eps1 = eps2 x (l1/l2) = 1.5 x (350/300) = 1.5 x 1.1667 ~ 1.75 V` },
  { section: "Wheatstone Bridge & Potentiometer", title: "Potentiometer — measuring internal resistance of a cell",
    explanation: "To measure a cell's internal resistance r with a potentiometer: first balance the cell on OPEN circuit to get length l1 (this measures its EMF epsilon = k.l1, since no current is drawn at balance). Then connect a known resistance R across the cell's terminals (closing the circuit so the cell delivers current) and rebalance to get a new, shorter length l2 (this measures the terminal voltage V = k.l2, which is less than epsilon). Since epsilon/V = (R+r)/R, the internal resistance is r = R(l1-l2)/l2.",
    example: `eps = k.l1 (open circuit)      V = k.l2 (closed circuit, with R)
eps/V = (R+r)/R   ->   r = R (l1 - l2) / l2

Known R = 10 Ohm
Open-circuit balance length   l1 = 300 cm
Closed-circuit balance length l2 = 250 cm

r = 10 x (300-250)/250 = 10 x 50/250 = 500/250 = 2 Ohm` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh18Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh18Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh18Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH18_QUESTIONS : NEET_PHYSICS_CH18_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 18</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Current Electricity</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 12 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH18_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH18_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
